<template>
  <hx-app
    id="inspire"
    :side-nav-width="sideNavWidth"
    :customer-logo-uri="customerLogoUri"
    :customer-logo-width="250"
    :customer-name="customerName"
    :show-header="showHeader"
    :version="version"
    :header-profile="headerProfile"
    homebar-id="policy-connect-hx-homebar"
    :name="headerUserName"
    :email="headerUserEmail"
    :show-site-settings="false"
    :show-beta-tester="false"
    :product-logo="policyConnectLogo"
    :product-text="productText"
    :hide-help-button="!isLoggedIn"
    :hide-avatar-menu="!isLoggedIn"
    :show-loading="loading"
    :show-header-container="true"
    @product-logo-click="goToHome"
    @feedback-click="showFeedbackView"
    @about-click="openAboutIdeagen"
    @sign-out-click="logout"
  >
    <template #right-side-nav>
      <side-panel-search v-if="isLoggedIn && !isFullScreen" @update-nav="NavTreeUpdate" />
    </template>

    <template #header-beneath-homebar>
      <cs-header-container
        id="policy-connect-header-container"
        :variant="headerContainerVariant"
        :label="clientDisplayName"
        :chip-props="groupSiteChipProps"
        :items="childSitesItems"
        :show-chip="!!groupSiteChipProps"
        @header-click="goToHome"
        @update:option-select-value="onChildSiteSelect"
      >
        <template #actions>
          <div class="policy-header-nav-buttons">
            <hx-button
              v-if="showBackToSupportSite"
              id="app-header--back-to-support"
              :label="actionButtonLabel('Back to support site')"
              left-icon="mdi-arrow-left"
              variant="secondary"
              @click="backToSupportSite"
            />

            <hx-button
              v-if="showLoginButton"
              id="app-header--login"
              :label="actionButtonLabel('Login')"
              left-icon="mdi-login"
              variant="primary"
              @click="goToLogin"
            />

            <hx-button
              v-if="showCreateButton"
              id="app-header--create"
              :label="actionButtonLabel('Create')"
              left-icon="mdi-file-document-plus-outline"
              :disabled="!draftMode"
              variant="primary"
              @click="openCreateDialog"
            />

            <div v-if="showDraftToggle" class="policy-header-toggle-wrapper">
              <v-icon icon="$i-file-edit" class="toggle-icon" />
              <span v-if="!isCompactHeader" class="toggle-label">Draft</span>
              <hx-toggle-switch
                id="app-header--draft"
                v-model="draftMode"
                @update:model-value="handleDraftToggle"
              />
            </div>

            <div v-if="showAdminToggle" class="policy-header-toggle-wrapper">
              <v-icon class="toggle-icon">mdi-account-cog-outline</v-icon>
              <span v-if="!isCompactHeader" class="toggle-label">Admin</span>
              <hx-toggle-switch
                id="app-header--admin"
                v-model="adminMode"
                @update:model-value="handleAdminToggle"
              />
            </div>

            <hx-button
              v-if="showNavSettingsButton"
              id="app-header--settings"
              :label="actionButtonLabel('Settings')"
              left-icon="mdi-cog"
              :variant="isPreferencesView ? 'primary' : 'secondary'"
              @click="showSettingsView"
            />

            <hx-button
              v-if="isLoggedIn"
              id="app-header--search"
              :label="actionButtonLabel('Search')"
              left-icon="mdi-magnify"
              :variant="isSearchPanelView ? 'primary' : 'secondary'"
              @click="showSearch"
            />

            <hx-button
              v-if="mazlanDisplayed"
              id="app-header--mazlan-ai-btn"
              :label="actionButtonLabel('Mazlan')"
              left-icon="$i-mazlan"
              variant="primary"
              @click="openMazlanDialog"
            />
          </div>

          <create-dialog
            v-if="isLoggedIn"
            :show-dialog="createDialogDisplayed"
            @closed="createDialogDisplayed = false"
          />
        </template>
      </cs-header-container>
    </template>

    <template #side-nav>
      <app-side-nav v-if="isLoggedIn && !isFullScreen" ref="nav"></app-side-nav>
      <app-side-nav-public
        v-if="!isLoggedIn && !isFullScreen && isPublicViewMode"
        ref="nav"
      ></app-side-nav-public>
    </template>

    <template #router-view>
      <refresh-token v-if="!isPublicViewMode"></refresh-token>
      <router-view v-slot="{ Component }">
        <v-slide-y-transition>
          <component :is="Component" @update-nav="NavTreeUpdate" />
        </v-slide-y-transition>
      </router-view>
      <div
        v-if="mazlanDisplayed"
        id="mazlan-ai-container"
        class="mazlan-ai-container"
        :data-dialog-open="mazlanDialogDisplayed"
      >
        <!-- ReactConnector will render DraggableContainer when dialog is open -->
      </div>
    </template>
  </hx-app>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import { CSBase } from '@complispace/cs-design-system';
import * as types from '@/store/mutationTypes';
import AppSideNav from '@/components/core/AppSideNav';
import AppSideNavPublic from '@/components/core/AppSideNavPublic';
import PageHistory from '@/components/core/PageHistory';
import RefreshToken from '@/components/core/RefreshToken';
import SidePanelSearch from '@/components/content/SidePanelSearch';
import routerUtils from '@/lib/routerUtils';
import { getUserRoleText } from '@/lib/util';
import sessionManager from '@/store/modules/session/sessionManager';
import {
  COMPONENT_STATE,
  APP_STATE,
  COMPONENT_MANAGER_TYPE,
  VIEW_TYPES,
  LINKS,
  ROLES
} from '@/constants';
import policyConnectLogo from '@/resources/assets/compliance_ideagen-policy-logic-policy-connect-darkmode.svg';
import pkg from '../package.json';
import CreateDialog from './components/content/CreateDialog';
import { ReactConnector } from './components/mazlan/Connector.tsx';

const PRODUCT_TEXT_MAP = {
  complispaceprimary: 'CompliSpace Primary',
  policyconnect: 'Policy Connect',
  complicare: 'CompliCare'
};

export default {
  name: 'App',

  components: {
    AppSideNav,
    AppSideNavPublic,
    PageHistory,
    SidePanelSearch,
    RefreshToken,
    CreateDialog
  },

  extends: CSBase,

  emits: ['setAppState'],

  data() {
    return {
      policyConnectLogo,
      loading: false,
      items: [],
      search: null,
      profileDisplayed: false,
      createDialogDisplayed: false,
      mazlanDialogDisplayed: false,
      draftMode: false,
      adminMode: false,
      mazlanConnector: null // Track the Mazlan AI connector instance
    };
  },

  computed: {
    ...mapState({
      app: (state) => state.app,
      cursor: (state) => state.appearance.cursor,
      isFullScreen: (state) => state.appearance.isFullScreen,
      isPublicViewMode: (state) => state.appearance.isPublicViewMode,
      isOnAhaPage: (state) => state.appearance.isOnAhaPage,
      profile: (state) => state.session.profile,
      appearance: (state) => state.appearance,
      client: (state) => state.client,
      role: (state) => state.authorisation.role,
      originalAdminRole: (state) => state.authorisation.originalAdminRole,
      clientAccessDetails: (state) => state.client.accessDetails,
      isPreferencesView: (state) => state.appearance.isPreferencesView,
      viewType: (state) => state.authorisation.viewType,
      isMobile: (state) => state.appearance.isMobile,
      isSearchPanelView: (state) => state.appearance.showSearchPanel
    }),

    ...mapGetters({
      contextItems: 'clientContext/items',
      contextValues: 'clientContext/values',
      getContextItem: 'clientContext/getContextItem',
      isLoggedIn: 'authorisation/isLoggedIn',
      isSupportMode: 'session/isSupportMode',
      targetSite: 'session/targetSite',
      isAllowedTo: 'authorisation/isAllowedTo',
      hasAccess: 'authorisation/hasAccess',
      isAdminDraftView: 'authorisation/isAdminDraftView',
      authorizationUri: 'session/authorizationUri',
      authorizationUriComplispaceStaff: 'session/authorizationUriComplispaceStaff',
      appName: 'app/name',
      userLoginMessage: 'client/userLoginMessage',
      isMazlanAdminFeaturesEnabled: 'client/isMazlanAdminFeaturesEnabled',
      isMazlanClientFeaturesEnabled: 'client/isMazlanClientFeaturesEnabled',
      isFirstLogin: 'session/isFirstLogin',
      isLoginBannerShown: 'session/isLoginBannerShown',
      auth0User: 'cs-session/user',
      hubUser: 'hub-session/user',
      hubConfig: 'hub/hubConfig',
      isHubEnabled: 'hub/isEnabled',
      isAuth0Enabled: 'authorisation/auth0Enabled'
    }),

    isAdminView() {
      return this.viewType !== VIEW_TYPES.CLIENT;
    },

    // Collapse header action buttons to icon-only on small screens so they all stay visible.
    isCompactHeader() {
      return this.$vuetify.display.smAndDown;
    },

    showBackToSupportSite() {
      return this.isLoggedIn && this.isSupportMode && !!this.targetSite;
    },

    showLoginButton() {
      return !this.isLoggedIn && this.isPublicViewMode;
    },

    showAdminToggle() {
      return this.isLoggedIn && this.isAdminRole;
    },

    showCreateButton() {
      return this.isLoggedIn && this.isAdminView && this.draftNavigationDisplayed;
    },

    showDraftToggle() {
      return (
        this.isLoggedIn && this.isAdminRole && this.isAdminView && this.draftNavigationDisplayed
      );
    },

    showNavSettingsButton() {
      return this.isLoggedIn && this.isAdminView && this.hasAccess('contextviewaccessTable');
    },

    clientDisplayName() {
      return this.client.displayName || 'Your PolicyConnect Site';
    },

    customerLogoUri() {
      return this.isPreferencesView ? null : this.client.logoUri || '';
    },

    customerName() {
      return this.isPreferencesView ? null : this.clientDisplayName;
    },

    version() {
      const { bundleVersion } = pkg;
      const uiVersion = pkg.version;
      const dashApiVersion = this.app.apiVersion ? `-${this.app.apiVersion}` : '';
      const contentEngineVersion = this.app.ceVersion ? `-${this.app.ceVersion}` : '';
      return `${bundleVersion}#${uiVersion}${dashApiVersion}${contentEngineVersion}`;
    },

    headerProfile() {
      const role = this.originalAdminRole ? getUserRoleText(this.originalAdminRole) : undefined;
      return {
        email: this.headerUserEmail,
        role
      };
    },

    headerUserName() {
      let name;

      if (this.isHubEnabled) {
        name = [this.hubUser?.name, this.hubUser?.surname].filter(Boolean).join(' ');
      } else if (this.isAuth0Enabled) {
        name = [this.auth0User?.name, this.auth0User?.surname].filter(Boolean).join(' ');
      } else {
        name = [this.profile?.name?.givenName, this.profile?.name?.familyName]
          .filter(Boolean)
          .join(' ');
      }

      return name || this.headerUserEmail.split('@')[0];
    },

    headerUserEmail() {
      return this.hubUser?.email || this.auth0User?.email || this.profile.email;
    },

    sideNavWidth() {
      if (this.isOnAhaPage) {
        return 0;
      }

      if (this.isLoggedIn) {
        return 400;
      }

      if (this.isPublicViewMode) {
        return 350;
      }

      return 0;
    },

    draftNavigationDisplayed() {
      const { isUgcEnabled } = this.clientAccessDetails;
      const result =
        isUgcEnabled &&
        !this.isPreferencesView &&
        (this.isAllowedTo(4) || this.isAllowedTo(3) || this.isAllowedTo(2));

      return result;
    },

    hasMazlanAdminFeaturesAccess() {
      return this.isMazlanAdminFeaturesEnabled && this.isAdminRole;
    },

    mazlanDisplayed() {
      return (
        this.isLoggedIn && (this.hasMazlanAdminFeaturesAccess || this.isMazlanClientFeaturesEnabled)
      );
    },

    shouldDisplayLoginBanner() {
      return this.userLoginMessage && this.isFirstLogin && !this.isLoginBannerShown;
    },

    isAdminRole() {
      if (
        this.role > ROLES.CLIENT_USER ||
        (this.role === ROLES.CLIENT_USER && this.originalAdminRole > ROLES.CLIENT_USER)
      ) {
        return true;
      }

      if (
        this.isAllowedTo(ROLES.CS_ADMIN) ||
        this.isAllowedTo(ROLES.GROUP_ADMIN) ||
        this.isAllowedTo(ROLES.CLIENT_ADMIN)
      ) {
        return true;
      }

      return false;
    },

    isGroupAdminRole() {
      if (
        this.role >= ROLES.GROUP_ADMIN ||
        (this.role === ROLES.CLIENT_USER && this.originalAdminRole >= ROLES.GROUP_ADMIN)
      ) {
        return true;
      }
      if (this.isAllowedTo(ROLES.CS_ADMIN) || this.isAllowedTo(ROLES.GROUP_ADMIN)) {
        return true;
      }
      return false;
    },

    isGroupParent() {
      return this.client.children && this.client.children.length > 0;
    },

    isGroupChild() {
      return this.client?.parentId !== null && this.client?.parentId !== undefined;
    },

    showHeader() {
      return !this.isOnAhaPage && (this.isLoggedIn || this.isPublicViewMode);
    },

    productText() {
      const appNameLowerCaseNoSpaces = (this.appName || '').replace(/\s+/g, '').toLowerCase();
      const matchedKey = Object.keys(PRODUCT_TEXT_MAP).find((key) =>
        appNameLowerCaseNoSpaces.includes(key)
      );
      const productText = matchedKey ? PRODUCT_TEXT_MAP[matchedKey] : 'Policy Connect';
      return productText.length > 15 ? `Ideagen\n${productText}` : `Ideagen ${productText}`;
    },

    isGroupAdminInParentSite() {
      return this.isGroupAdminRole && !!this.isGroupParent;
    },

    isGroupAdminInChildSite() {
      return this.isGroupAdminRole && !!this.isGroupChild;
    },

    headerContainerVariant() {
      return this.isGroupAdminInParentSite ? 'regular' : 'lite';
    },

    groupSiteChipProps() {
      if (this.isGroupAdminInParentSite) {
        return {
          color: 'teal',
          label: 'Parent site'
        };
      } else if (this.isGroupAdminInChildSite) {
        return {
          color: 'pink',
          label: 'Child site'
        };
      }
      return null;
    },

    childSitesItems() {
      if (!this.isGroupAdminInParentSite) return [];
      return (this.client.children || [])
        .filter((child) => child.parentId === this.client.id && child.siteUri && child.isActive)
        .map((child) => ({ title: child.displayName || child.name, value: child.siteUri }));
    }
  },

  watch: {
    viewType(newValue) {
      this.adminMode = newValue !== VIEW_TYPES.CLIENT;
    },

    isAdminDraftView(newValue) {
      this.draftMode = newValue;
    },

    contextValues() {
      const CSPColours = this.getContextItem('CSPColours');
      if (CSPColours) {
        const contextTheme = CSPColours.value;
        try {
          const ctp = JSON.parse(contextTheme);
          if (ctp && ctp.primary) this.$vuetify.theme = ctp;
        } catch (e) {
          console.log('Unable to process CSPColours : ', e);
        }
      }
    },
    cursor(value) {
      switch (value) {
        case 'progress':
          document.body.classList.add('busy-cursor');
          break;
        default:
          document.body.classList.remove('busy-cursor');
          break;
      }
    },
    shouldDisplayLoginBanner(newValue) {
      if (newValue) {
        const { message, options } = this.parseUserLoginMessageForAlert(this.userLoginMessage);
        this.showInfoAlert(message, false, options);
        this.setIsLoginBannerShown(true);
        setTimeout(() => {
          this.clearAlert(); // close info alert after 15 seconds
        }, 15000);
      }
    },

    mazlanDisplayed(newValue) {
      if (newValue) {
        // Mazlan should be displayed, initialize it
        this.$nextTick(() => {
          this.initializeMazlanConnector();
        });
      } else {
        // Mazlan should be hidden, clean it up
        this.cleanupMazlanConnector();
      }
    },

    mazlanDialogDisplayed(newValue) {
      if (this.mazlanConnector) {
        // Update existing connector with new state
        this.updateMazlanConnector();
      } else if (newValue) {
        // Dialog opened - initialize the React component
        this.$nextTick(() => {
          this.initializeMazlanConnector();
        });
      }
    }
  },

  async mounted() {
    // Explicitly set the legacy IDP token expiry from localStorage so that
    // authorisation/isLoggedIn is not incorrectly false if checked before the expiry is initialised.
    this.$store.commit(
      types.SESSION_SET_TOKEN_EXPIRY,
      sessionManager.getTokenExpiryFromLocalStorage()
    );
    // Pre-fetch hub config so hub/isEnabled is correctly set. Skipped if already fetched
    if (this.$store._actions['hub/getHubConfigByHostname'] && !this.hubConfig) {
      try {
        await this.$store.dispatch('hub/getHubConfigByHostname');
      } catch (err) {
        // errors are handled by CSApp/CSLogin components
        console.error('Failed to get hub config:', err.message);
      }
    }
    // Start up procedure
    const { plugIns } = this.$app;
    plugIns.initialPlugIn();

    try {
      let applicationName = sessionManager.getAppNameFromStorage();
      if (!applicationName) {
        applicationName = await this.$store.dispatch('getAppNameFromHost');
      }
      document.title = applicationName;
      await this.$store.dispatch('appInitialise', pkg.version, { root: true });
      await this.$store.dispatch('initAppName', applicationName, { root: true });
      if (document.title !== this.appName) document.title = this.appName;
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        this.showErrorResponseAlert(err);
      }

      if (err.message === 'Network Error') {
        this.$router.push('servernotavailable').catch(routerUtils.handleNavigationFailures);
      } else {
        this.$router.push('somethingwentwrong').catch(routerUtils.handleNavigationFailures);
      }
      console.error(err);
    }

    if (
      navigator.userAgent.match(
        /(phone|pad|pod|iPhone|iPod|ios|iPad|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone)/i
      )
    ) {
      this.$store.commit(types.APPEARANCE_SET_ISMOBILE, true);
    }

    this.$nextTick(() => {
      setTimeout(() => {
        if (this.isPublicViewMode) {
          this.setShowSideNav(false);
        }
      }, 5000);
    });

    this.draftMode = this.isAdminDraftView;
    this.adminMode = this.viewType !== VIEW_TYPES.CLIENT;

    // Initialize Mazlan AI connector when app mounts
    this.$nextTick(() => {
      this.initializeMazlanConnector();
    });
  },

  beforeUnmount() {
    // Clean up Mazlan connector when component is destroyed
    this.cleanupMazlanConnector();
  },

  methods: {
    async logout() {
      await this.$store.dispatch('authorisation/logout');
      if (!this.isHubEnabled) {
        await this.$router.go('/');
      }
    },

    NavTreeUpdate(details) {
      this.$refs.nav.treeUpdate(details);
    },

    getAppState() {
      let res = APP_STATE.STATE_IDLE;
      if (this.$refs && this.$refs.nav && !this.isPublicViewMode) {
        const comState = this.$refs.nav.getComponentState();
        if (comState === COMPONENT_STATE.STATE_EDITING) {
          res = APP_STATE.STATE_EDITING;
        }
      }
      return res;
    },

    setAppState(state) {
      if (this.$refs && this.$refs.nav) {
        if (state === APP_STATE.STATE_IDLE) {
          this.$refs.nav.updateComponent(COMPONENT_MANAGER_TYPE.TYPE_COM_STATE, {
            state: COMPONENT_STATE.STATE_IDLE
          });
        }
      }
    },

    ...mapMutations({
      setSearchPanel: types.APPEARANCE_SET_SEARCH_PANEL,
      setIsFirstLogin: types.SESSION_SET_IS_FIRST_LOGIN,
      setIsLoginBannerShown: types.SESSION_SET_IS_LOGIN_BANNER_SHOWN
    }),

    showSearch() {
      this.setSearchPanel(true);
    },

    // Hide the text label on small screens, leaving an icon-only button.
    // Full load so every store and CSDS restart on the support site.
    backToSupportSite() {
      sessionManager.clearTenantScopedState();
      this.$store.commit(types.SESSION_SET_TARGET_SITE, null);
      window.location.assign('/');
    },

    actionButtonLabel(label) {
      return this.isCompactHeader ? '' : label;
    },

    showSettingsView() {
      if (this.editingCheck()) {
        this.$emit('setAppState', APP_STATE.STATE_IDLE);
        this.$router.push('/preferences/context').catch(routerUtils.handleNavigationFailures);
      }
    },

    initializeMazlanConnector() {
      // Check if mazlan should be displayed
      if (!this.mazlanDisplayed) {
        return;
      }

      try {
        // Clean up any existing instance first
        if (this.mazlanConnector) {
          this.mazlanConnector.unmount();
          this.mazlanConnector = null;
        }

        const mazlanContainer = document.getElementById('mazlan-ai-container');
        if (!mazlanContainer) {
          setTimeout(() => this.initializeMazlanConnector(), 100);
          return;
        }

        // Create React app - it manages itself from now on
        this.mazlanConnector = new ReactConnector(mazlanContainer);
        // Initial render with current state
        this.updateMazlanConnector();
      } catch (error) {
        console.error('Failed to initialize Mazlan AI Connector:', error);
      }
    },

    updateMazlanConnector() {
      if (this.mazlanConnector) {
        const tokenAction = this.isHubEnabled ? 'hub-session/getToken' : 'cs-session/getToken';
        this.$store
          .dispatch(tokenAction)
          .then((vueToken) => {
            this.mazlanConnector.render({
              open: this.mazlanDialogDisplayed,
              token: vueToken, // Pass Vue token as default
              hasMazlanAdminFeaturesAccess: this.hasMazlanAdminFeaturesAccess,
              onClose: () => {
                this.mazlanDialogDisplayed = false;
              }
            });
          })
          .catch((error) => {
            console.error('Failed to update Mazlan AI Connector:', error);
          });
      }
    },

    cleanupMazlanConnector() {
      if (this.mazlanConnector) {
        try {
          this.mazlanConnector.unmount();
          this.mazlanConnector = null;
        } catch (error) {
          console.error('Failed to cleanup Mazlan AI Connector:', error);
        }
      }
    },

    showContextView() {
      this.$router.push('/preferences/context').catch(routerUtils.handleNavigationFailures);
    },

    showGroupAdminView() {
      this.$router.push('/preferences/groupAdmin').catch(routerUtils.handleNavigationFailures);
    },

    showFeedbackView() {
      if (this.editingCheck()) {
        this.$emit('setAppState', APP_STATE.STATE_IDLE);
        this.$router.push('/feedback').catch(routerUtils.handleNavigationFailures);
      }
    },

    openAboutIdeagen() {
      this.openUrlInNewTab(LINKS.ABOUT_IDEAGEN);
    },

    handleAdminToggle(newValue) {
      try {
        if (newValue) {
          // Admin toggle always starts clean: draft state from a previous admin
          // session (or stale localStorage after a hard refresh) must not leak in.
          this.$store.dispatch('authorisation/saveDraftMode', false);
          this.$store.dispatch('authorisation/saveUserRole', this.originalAdminRole);
        } else if (this.editingCheck()) {
          this.$store.dispatch('authorisation/saveDraftMode', false);
          this.$store.dispatch('authorisation/saveUserRole', ROLES.CLIENT_USER);
        } else {
          this.$nextTick(() => {
            this.adminMode = true;
          });
        }
      } catch (err) {
        console.error(err);
      }
    },

    handleDraftToggle(newValue) {
      if (!newValue) {
        this.$store.dispatch('authorisation/saveDraftMode', false);
      } else if (this.editingCheck()) {
        this.$store.dispatch('authorisation/saveDraftMode', true);
      } else {
        this.$nextTick(() => {
          this.draftMode = false;
        });
      }
    },

    goToLogin() {
      this.$router.push({ name: 'login' }).catch(routerUtils.handleNavigationFailures);
    },

    goToHome() {
      if (this.$router.currentRoute.name === 'Dashboard') {
        // give loading impression although nothing is really to update when clicking
        // Home on Home (Dashboard) page
        this.setLoading(true);

        this.$router.push('/').catch(routerUtils.handleNavigationFailures);

        setTimeout(() => {
          this.setLoading(false);
        }, 300);

        return;
      }

      this.$router.push('/').catch(routerUtils.handleNavigationFailures);
    },

    goBack() {
      this.$store.commit(types.APPEARANCE_SET_FULLSCREEN, false);
      this.$router.back();
    },

    profileToggle() {
      this.profileDisplayed = !this.profileDisplayed;
    },

    openCreateDialog() {
      this.createDialogDisplayed = true;
    },

    openMazlanDialog() {
      this.mazlanDialogDisplayed = true;
    },

    editingCheck() {
      let isContinue = true;

      // TODO: Unify Interface over adminRole judgement
      // the current situation is use isAllowedTo(4) || isAllowedTo(3) || isAllowedTo(2) which is redundant but used everywhere
      // isAdminRole seems better but with a vague property originalAdminRole
      if (
        this.appState === APP_STATE.STATE_EDITING &&
        (this.isAllowedTo(ROLES.CS_ADMIN) ||
          this.isAllowedTo(ROLES.GROUP_ADMIN) ||
          this.isAllowedTo(ROLES.CLIENT_ADMIN))
      ) {
        isContinue = window.confirm(
          'Do you really want to leave?\nChanges that you made may not be saved.'
        );
      }
      return isContinue;
    },

    openUrlInNewTab(url) {
      window.open(url, '_blank');
    },

    /**
     * Parses userLoginMessage for alert display.
     * Supports optional embedded URL inside <func> or <href> tags:
     *   <func>Click here|https://example.com</func>
     *   <href>Click here|https://example.com</href>
     * The URL is stripped from the tag and passed to showInfoAlert via options.
     */
    parseUserLoginMessageForAlert(message) {
      if (!message) {
        return { message: null, options: undefined };
      }

      const tagWithUrlMatch = message.match(
        /<(?<tag>func|href)>(?<label>[^|]+)\|(?<url>https?:\/\/[^<]+?)<\/\k<tag>>/
      );

      if (tagWithUrlMatch) {
        const { tag, label, url } = tagWithUrlMatch.groups;
        const linkUrl = url.trim();
        const displayMessage = message.replace(
          tagWithUrlMatch[0],
          `<${tag}>${label.trim()}</${tag}>`
        );

        if (tag === 'href') {
          return {
            message: displayMessage,
            options: { href: linkUrl }
          };
        }

        return {
          message: displayMessage,
          options: {
            func: () => this.openUrlInNewTab(linkUrl)
          }
        };
      }

      return { message, options: undefined };
    },

    onChildSiteSelect(title) {
      const item = this.childSitesItems.find((i) => i.title === title);
      if (item) this.openUrlInNewTab(item.value);
    }
  }
};
</script>

<style>
* {
  margin: 0;
  padding: 0;
}

html,
body {
  height: 100%;
}

/*
    body {
        cursor: url('./resources/assets/cursor.png'), auto;
    }
    */

body.busy-cursor {
  cursor: progress;
}

.vertical-center {
  display: flex;
  align-items: center;
}

.bottom-container {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
}

a {
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
}

.center-it {
  background-position: center;
  justify-content: center;
  text-align: center;
  justify-content: center;
  align-items: center;
}

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.app-split-bar {
  background-color: transparent;
  width: 0px;
  z-index: 100;
  height: 100vh;
  top: 58px;
  margin-left: 360px;
  position: fixed;
}

.app-split-bar-collapsed {
  z-index: 3;
  height: 100vh;
  top: 58px;
  width: 20px;
  margin-left: 0px;
  position: fixed;
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}

.app-split-bar .app-split-bar-arrow {
  position: relative;
  top: 12px;
  left: 22px;
  z-index: 1;
}

.app-split-bar-collapsed .app-split-bar-arrow {
  position: relative;
  top: 12px;
  left: 2px;
  z-index: 1;
}

#main-container {
  background-color: #fff;
}

#cs-side-nav {
  background-color: #eff3f6;
}

/* CSSideNav always renders this VSpacer regardless of the version prop; without hiding it,
   it flex-grows into empty space now that main.css hides the version footer text (CSPOL-1958) */
#cs-side-nav .v-spacer {
  display: none;
}

/* Do not override Vuetify's flex: 1 0 100% — that lets panels wrap side-by-side */
#cs-side-nav .side-nav {
  margin-bottom: 0;
}

.footer {
  background-color: white !important;
}

/* helix v4 portals to <body> at z-index 1000; app header is 2000, dropdowns 3000,
   snackbar 10000. 10001 clears them all. */
/* Popper-based: dropdown, tooltip, popover */
body > [data-radix-popper-content-wrapper]:has(.helix-react-ai) {
  z-index: 10001 !important;
}

/* Sheet + Dialog content (role="dialog") */
.helix-react-ai[role='dialog'],
[data-slot='dialog-content'] {
  z-index: 10001 !important;
}

/* Overlays */
[data-slot='dialog-overlay'] {
  z-index: 10001 !important;
}

/* Vuetify's runtime .bg-* utilities share class names with helix-react-ai's Tailwind and
   force colour with !important. Re-assert helix's tokens at higher specificity. */
.helix-react-ai .text-hai-foreground {
  color: var(--hai-foreground) !important;
}

.helix-react-ai .text-hai-muted-foreground {
  color: var(--hai-muted-foreground) !important;
}

/* Vuetify's bg-* also force colour: .bg-transparent{color:currentColor!important} */
.helix-react-ai .text-hai-link {
  color: var(--hai-link) !important;
}

/* Same collision on background: code block, table header, even rows. */
.helix-react-ai .bg-secondary,
.helix-react-ai .even\:bg-secondary:nth-child(2n) {
  background-color: var(--hai-ui-secondary) !important;
}

.helix-react-ai .bg-background {
  background-color: var(--hai-ui-background) !important;
}

.helix-react-ai .group:hover .group-hover\:opacity-100,
.helix-react-ai .focus-visible\:opacity-100:focus-visible {
  opacity: 1 !important;
}

.helix-react-ai .hai-welcome-dots-bg {
  background-repeat: repeat;
}

/* Vuetify forces `thin solid currentColor/12%`. Per-side — shorthands draw all four. */
.helix-react-ai .border {
  border-width: 1px !important;
  border-style: solid !important;
}

.helix-react-ai .border-t {
  border-top-width: 1px !important;
  border-top-style: solid !important;
}

.helix-react-ai .border-b {
  border-bottom-width: 1px !important;
  border-bottom-style: solid !important;
}

.helix-react-ai .border-hai-border {
  border-color: var(--hai-border) !important;
}

.helix-react-ai .rounded-sm {
  border-radius: calc(var(--hai-ui-radius) - 4px) !important;
}
.helix-react-ai .rounded-xl {
  border-radius: calc(var(--hai-ui-radius) + 4px) !important;
}
</style>
<style scoped>
.v-tooltip__content--fixed {
  position: fixed;
}

.v-tooltip__content {
  z-index: 1500 !important;
  top: 72px !important;
}

.menuable__content__active {
  z-index: 1500 !important;
  top: 72px !important;
}
</style>

<style scoped>
:deep(.hx-content-card) {
  padding: 0;
  & .content-card-body {
    padding: 16px;
  }
}

:deep(.hx-homebar-horizontal__product-logo-label h3) {
  white-space: pre-line;
}

/*
  The design system positions the actions wrapper absolutely (right: 16px), which makes
  the buttons overlap the client display name on narrow screens. Let it flow inline and
  right-align via margin so buttons stay visible and never draw on top of the label.
*/
#policy-connect-header-container :deep(.cs-header-container__buttons) {
  position: static;
  margin-left: auto;
}

.policy-header-nav-buttons {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Icon, label, and switch sit directly on the page background (no pill wrapper). */
.policy-header-toggle-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #212d38;
  flex: 0 0 auto;
}

/* Prevent the Vuetify switch from growing and pushing adjacent header actions. */
.policy-header-toggle-wrapper :deep(.v-input) {
  flex: 0 0 auto;
}

.policy-header-toggle-wrapper .toggle-icon {
  width: 20px;
  height: 20px;
  font-size: 20px;
}

.policy-header-toggle-wrapper .toggle-label {
  font-family: 'Gilroy Bold', Verdana, sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 0;
}
</style>
